import type { Card, PromptContext, SpecState } from '../types';
import { languageInfo } from '../languages';

const MATLAB_GUIDELINES = `When you write MATLAB:
- Prefer vectorized operations and built-in functions over explicit loops where it stays readable.
- Use name=value argument syntax instead of 'Name','Value' pairs.
- Use datetime and duration instead of datenum and datestr; use tables for tabular data; prefer string arrays over char arrays for text.
- Use tiledlayout and nexttile instead of subplot; do not call figure before every plot.
- End every function with end, document each function with a leading comment, and mention any toolbox a function needs.`;

/**
 * The system prompt is the same for every card and every turn, apart from the user's standing instructions.
 * Anything that changes (language, code, spec, run output) travels in the messages instead, because a changed
 * system prompt breaks prompt caching and invalidates the model's saved reasoning.
 */
export function buildSystemPrompt(userPrompt: string): string {
  const sections: string[] = [
    'You are Cue, a pair programmer inside a browser coding workspace. The user works in cards. A card holds this conversation, one code file in an editor, and a spec: a short Markdown description of what the code does and how it should behave. Python, JavaScript and HTML run right in the browser; other languages the user runs elsewhere.',
    "Each of the user's messages carries the card as it was when they sent it, inside <card_context>: the code, the spec and whether it agrees with the code, and the output of a run when there is one. The latest message's context is the source of truth for what they have now.",
    `How to change things:
- Call propose_changes to change the code, the spec, or both, with complete contents. The user reviews each proposal as a diff.
- Keep the spec true. When a change alters what the code does, send the updated spec in the same proposal. When the user has edited the spec, treat it as their intent and bring the code in line with it.
- A spec is a few lines of Markdown: one line on the purpose, then bullets for behavior, inputs and outputs, and edge cases. It describes behavior, not implementation, and contains no code.
- Keep the prose around a proposal short: what changed and why. Do not repeat the code in the chat.
- Small illustrative snippets that are not meant for the editor can go in fenced code blocks.`,
    `How code runs when the user presses Run:
- Python runs in the browser with Pyodide, as a script: top-level code runs, printed output and matplotlib figures are shown. Packages it imports load on their own when Pyodide has them (numpy, pandas, matplotlib, pytest and more). There is no keyboard input, so do not call input().
- JavaScript runs as a module in a sandboxed page, and console output is shown. HTML is rendered as a page. There is no npm install.
- There is no test runner. Tests must run from the file itself: call the test functions at the bottom, or use plain asserts, and print a short summary so the output shows what passed.`,
    `How to talk:
- Follow the user's lead. If they are learning or ask how or why, teach: explain the idea first, go step by step, prefer a small example to a finished solution, and check what they understood. If they want a result, be brief and get to the proposal.
- Ask one short question instead of guessing when a choice really matters.
- Use Markdown, be direct, and keep the user's conventions (naming, formatting, libraries) unless asked to change them.`,
    MATLAB_GUIDELINES,
  ];
  if (userPrompt.trim()) sections.push(`Standing instructions from the user:\n${userPrompt.trim()}`);
  return sections.join('\n\n');
}

const SPEC_STATE_NOTE: Record<SpecState, string> = {
  empty: 'This card has no spec yet.',
  'in-step': 'The spec and the code agree.',
  'code-changed': 'The code was edited after the spec was last updated, so the spec may be out of date.',
  'spec-changed': 'The user edited the spec after the code was last updated, so the code may not do what the spec says yet.',
  unchecked: 'Nobody has checked this spec against the code yet.',
};

const MAX_OUTPUT = 4000;
const FENCE = '```';

/** The card state attached to a prompt, in a stable, clearly delimited format. */
export function formatContext(ctx: PromptContext): string {
  const parts: string[] = [];
  if (ctx.code) {
    const info = languageInfo(ctx.code.language);
    if (ctx.code.code.trim()) {
      const lines = ctx.code.code.split('\n').length;
      const where = info.runner ? 'runs in the browser' : 'runs outside the browser';
      parts.push(`Code (${info.label}, ${lines} line${lines === 1 ? '' : 's'}, ${where}):\n${FENCE}${ctx.code.language}\n${ctx.code.code}\n${FENCE}`);
    } else {
      parts.push(`Code: the editor is empty. The language is set to ${info.label}.`);
    }
  }
  if (ctx.spec) {
    const body = ctx.spec.text.trim() ? `\n<spec>\n${ctx.spec.text}\n</spec>` : '';
    parts.push(`Spec. ${SPEC_STATE_NOTE[ctx.spec.state]}${body}`);
  }
  if (ctx.output && ctx.output.text.trim()) {
    const text = ctx.output.text.length > MAX_OUTPUT ? `(earlier output cut)\n${ctx.output.text.slice(-MAX_OUTPUT)}` : ctx.output.text;
    parts.push(`Last run output (${ctx.output.failed ? 'the run failed' : 'the run finished'}):\n${FENCE}\n${text}\n${FENCE}`);
  }
  return `<card_context>\n${parts.join('\n\n')}\n</card_context>`;
}

/** Prompts behind the spec and run buttons. They go through the conversation like any other message. */
export const PROMPTS = {
  writeSpec: 'Write a spec for the code in this card, describing what it does and how it behaves. Propose only the spec.',
  updateSpec:
    'The code changed since the spec was written. Update the spec so it describes the current code. Propose only the spec, and point out anything in the code that looks like a bug.',
  checkSpec:
    'Check whether the spec still describes the code. If they agree, say so in one sentence and propose nothing. If they differ, say where, and propose the spec updated to fit the code. Point out anything in the code that looks like a bug, but only when you are sure.',
  followSpec: 'I edited the spec. Update the code so it does what the spec says, and propose the change.',
  fixRun: 'The last run failed. Find the cause and propose a fix.',
};

export function editSelectionMessage(instruction: string, selection: string, fromLine: number, toLine: number): string {
  return `${instruction}\n\nApply this only to lines ${fromLine}-${toLine} of the code (shown below) and leave the rest unchanged. Propose the complete updated code.\n\n${FENCE}\n${selection}\n${FENCE}`;
}

export const TITLE_SYSTEM =
  'You name cards in a coding workspace. Reply with only the title: at most six words, no quotes, no trailing period, no Markdown.';

export function titlePrompt(card: Card): string {
  const excerpt = card.messages
    .slice(0, 4)
    .map((m) => {
      const text = m.parts
        .filter((p) => p.type === 'text')
        .map((p) => p.text)
        .join(' ')
        .slice(0, 500);
      return `${m.role}: ${text}`;
    })
    .join('\n');
  return `Write a title for this card:\n\n${excerpt}`;
}

export interface SuggestionState {
  language: string;
  lastPrompt: string;
  lastReply: string;
  /** What happened to the latest proposal, if any. */
  proposal: 'none' | 'pending' | 'accepted' | 'rejected';
  specState: SpecState;
  hasCode: boolean;
  codeExcerpt: string;
  run: 'none' | 'ok' | 'failed';
  runOutput: string;
}

export const SUGGESTIONS_SYSTEM = `You suggest what a programmer might ask their AI pair programmer next, inside a browser coding workspace where each card holds a conversation, one code file and a spec. The user taps a suggestion to send it as their next prompt.

Write exactly three suggestions:
- Each is a prompt in the user's voice, imperative or a question, at most 60 characters.
- Make them specific to this code and this moment. Name the real functions, errors or features involved. Never generic ("Improve the code", "Add comments").
- Cover different directions: the obvious next step, a way to make the code more robust or complete (tests, edge cases, performance), and something to understand or explore.
- React to state: a failed run means one suggestion fixes that exact error; a pending proposal means one suggestion questions or adjusts it; a spec that is out of date means one suggestion brings it back in step; an empty editor means suggestions for what to build.
- Do not suggest running the code; the user has a Run button.
- Code runs in the browser with no test runner, no keyboard input and no package installs. Do not suggest what needs them (pytest discovery, input(), npm install).

Reply with JSON only: {"suggestions": ["...", "...", "..."]}`;

const clip = (text: string, max: number) => (text.length > max ? `${text.slice(0, max)}…` : text);

export function suggestionsPrompt(s: SuggestionState): string {
  return [
    `Language: ${languageInfo(s.language).label}`,
    `Latest prompt: ${clip(s.lastPrompt, 600) || '(none yet)'}`,
    `Latest reply: ${clip(s.lastReply, 1200) || '(none yet)'}`,
    `Latest proposal: ${s.proposal}`,
    `Spec: ${SPEC_STATE_NOTE[s.specState]}`,
    s.hasCode ? `Code (excerpt):\n${clip(s.codeExcerpt, 3000)}` : 'Code: the editor is empty.',
    s.run === 'none' ? 'Run: not run yet.' : `Run: ${s.run === 'failed' ? 'failed' : 'succeeded'}. Output tail:\n${clip(s.runOutput.slice(-800), 800)}`,
  ].join('\n\n');
}

export const SUGGESTIONS_SCHEMA = {
  type: 'object',
  properties: {
    suggestions: { type: 'array', items: { type: 'string' } },
  },
  required: ['suggestions'],
  additionalProperties: false,
} as const;

/** Clean up whatever came back: strings only, trimmed, short, unique, three at most. */
export function cleanSuggestions(value: unknown): string[] {
  const list =
    value && typeof value === 'object' && Array.isArray((value as { suggestions?: unknown }).suggestions)
      ? (value as { suggestions: unknown[] }).suggestions
      : Array.isArray(value)
        ? value
        : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of list) {
    if (typeof item !== 'string') continue;
    // Cues are prompts, not sentences: drop list markers, quotes and a closing period (a question mark stays).
    const text = item.replace(/^[-*\d.\s"']+|["'\s]+$/g, '').replace(/(?<!\.)\.$/, '').trim();
    if (!text || text.length > 90 || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
    if (out.length === 3) break;
  }
  return out;
}
